import dayjs from 'dayjs';
import { CustomerTier, OrderQuotaDetail, OrderQuotaRow, OrderQuotaStrategy } from '../graphql/orderQuotaGraphql';

/** 单个配额的上限：不设业务上限，只受数据库 INT 列与 GraphQL Int 限制，与后端校验一致 */
export const MAX_QUOTA = 2_147_483_647;

const DATE_FORMAT = 'YYYY-MM-DD';

/**
 * 日期列。日期一律是美西自然日的 yyyy-MM-dd 字符串，只做纯日期计算，不经过浏览器本地时区换算。
 */
export interface DateColumn {
    date: string;
    isWeekend: boolean;
    isToday: boolean;
    isPast: boolean;
    /** 周一，网格在其前面画分隔线 */
    isWeekStart: boolean;
}

export const shiftDate = (date: string, days: number): string => dayjs(date).add(days, 'day').format(DATE_FORMAT);

export const buildDateColumns = (startDate: string, endDate: string, today: string): DateColumn[] => {
    const columns: DateColumn[] = [];
    for (let date = startDate; date <= endDate; date = shiftDate(date, 1)) {
        const weekday = dayjs(date).day();
        columns.push({
            date,
            isWeekend: weekday === 0 || weekday === 6,
            isToday: date === today,
            isPast: date < today,
            isWeekStart: weekday === 1,
        });
    }
    return columns;
};

export const overrideOf = (row: OrderQuotaRow, date: string): number | undefined =>
    row.overrides.find((item) => item.date === date)?.dailyQuota;

/** 一段单独设置的日期：连续且值相同的日期合并为一段 */
export interface OverrideRange {
    start: string;
    end: string;
    dailyQuota: number;
}

/**
 * 行在今日及以后的按日产能，连续且值相同的日期合并为一段，按日期升序；首列据此提示网格之外的单独设置。
 * 详情接口除查询区间外还返回今日及以后的全部按日产能。
 */
export const upcomingOverrideRanges = (row: OrderQuotaRow, today: string): OverrideRange[] =>
    row.overrides
        .filter((item) => item.date >= today)
        .sort((a, b) => a.date.localeCompare(b.date))
        .reduce<OverrideRange[]>((ranges, item) => {
            const last = ranges[ranges.length - 1];
            if (last && last.dailyQuota === item.dailyQuota && shiftDate(last.end, 1) === item.date) {
                last.end = item.date;
            } else {
                ranges.push({ start: item.date, end: item.date, dailyQuota: item.dailyQuota });
            }
            return ranges;
        }, []);

/**
 * 行在没有按日产能时沿用的值：行日产能；未配置时为 null（分拣为「未设置」；尾程为不限）。
 */
export const fallbackQuota = (row: OrderQuotaRow): number | null =>
    row.dailyQuota !== null && row.dailyQuota !== undefined ? row.dailyQuota : null;

/** 未配置是否表示不限：尾程产能管理是（未配置的 Warehouse 不限）；分拣产能管理的分级客户必须填值。两个策略的 0 都表示直接拦截 */
export const unsetMeansUnlimited = (strategyType: number): boolean => strategyType !== OrderQuotaStrategy.CUSTOMER_PICKUP_HUB;

/** 行的前端上限：分拣产能管理的低优上限不能超过基础产能；高优保障值的容量校验由后端完成 */
export const rowMaxValue = (row: OrderQuotaRow | undefined, detail: OrderQuotaDetail): number | undefined =>
    detail.strategyType === OrderQuotaStrategy.CUSTOMER_PICKUP_HUB && row?.customerTier === CustomerTier.LOW
        ? detail.hubDailyQuota
        : undefined;

export type QuotaParseResult =
    | { ok: true; value: number | null }
    | { ok: false; reason: 'notInteger' | 'tooLarge' | 'overBase' };

/**
 * 解析单元格输入：空串表示清空（仅 allowEmpty 时合法）；须为 0 ~ 2,147,483,647 的整数；
 * 给出 maxValue 时不能超过它（低优上限 ≤ 基础产能）。
 */
export const parseQuotaInput = (raw: string, allowEmpty: boolean, maxValue?: number): QuotaParseResult => {
    const text = raw.replace(/,/g, '').trim();
    if (text === '') {
        return allowEmpty ? { ok: true, value: null } : { ok: false, reason: 'notInteger' };
    }
    if (!/^\d+$/.test(text)) return { ok: false, reason: 'notInteger' };
    const value = Number(text);
    if (value > MAX_QUOTA) return { ok: false, reason: 'tooLarge' };
    if (maxValue !== undefined && value > maxValue) return { ok: false, reason: 'overBase' };
    return { ok: true, value };
};

/** 以千分位展示配额 */
export const formatQuota = (value: number): string => value.toLocaleString('en-US');

/** 在本地数据中替换某一行，保持其余行引用不变 */
export const replaceRow = (
    rows: OrderQuotaRow[],
    targetId: string,
    update: (row: OrderQuotaRow) => OrderQuotaRow
): OrderQuotaRow[] => rows.map((row) => (row.targetId === targetId ? update(row) : row));

/** 设置或清除某几天的按日配额（本地数据就地更新） */
export const applyOverrides = (row: OrderQuotaRow, dates: string[], dailyQuota: number | null): OrderQuotaRow => {
    const kept = row.overrides.filter((item) => !dates.includes(item.date));
    const added = dailyQuota === null ? [] : dates.map((date) => ({ date, dailyQuota }));
    return { ...row, overrides: [...kept, ...added].sort((a, b) => a.date.localeCompare(b.date)) };
};
